import { graphql } from 'gatsby'
import React from 'react'

import { FinanceQueryQuery } from '../../types/graphql-types'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import Appointment from '../components/appointment'

interface Props {
  data: FinanceQueryQuery
  location: Location
}

const Finance: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Finance Options - Pay Later with Payl8r | Ada Aesthetics Cheshunt"
        description="Spread the cost of your aesthetic treatments with Payl8r. Flexible payment plans up to 12 months. Available at Ada Aesthetics, Cheshunt, Hertfordshire."
        keywords="Payl8r aesthetics, pay later Botox, finance lip fillers, payment plans aesthetic treatments, Cheshunt, interest free"
        url="https://adaaesthetics.uk/finance"
        site={meta}
      />

      <section className="page_title ls s-py-90 s-py-xl-120 s-overlay">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h1 className="special-heading">
                <span>Finance</span> Options
              </h1>
              <p className="text-white">
                Spread the cost of Botox, fillers and aesthetic treatments at Ada Aesthetics in Cheshunt with flexible Payl8r payment plans.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Payl8r Section */}
      <section className="ls s-pt-50 s-pb-50 s-pt-lg-100 s-pb-lg-100">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10">
              <div className="row align-items-center mb-5">
                <div className="col-md-4 text-center mb-3 mb-md-0">
                  <img
                    src="/img/finance_payl8r.jpeg"
                    alt="Payl8r - Now available at checkout. Spread the cost your way with longer terms available."
                    style={{
                      maxWidth: '200px',
                      width: '100%',
                      borderRadius: '10px',
                    }}
                  />
                </div>
                <div className="col-md-8">
                  <h2 className="special-heading mt-0">
                    <span>Payl8r</span> Now Available
                  </h2>
                  <p>
                    Spread the cost your way with longer terms available. We've
                    partnered with <strong>Payl8r</strong> so you can get your
                    treatment now and pay later with flexible monthly payments.
                  </p>
                </div>
              </div>

              {/* Payment Options */}
              <div className="row mt-5">
                <div className="col-md-6 mb-4">
                  <div
                    className="text-center p-4"
                    style={{
                      background: '#f8f9fa',
                      borderRadius: '10px',
                      height: '100%',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '36px',
                        color: '#9b59b6',
                        marginBottom: '15px',
                      }}
                    >
                      <i className="fa fa-clock-o" />
                    </div>
                    <h4>Payl8r Zero</h4>
                    <p>
                      Spread your payments over <strong>30, 60, or 90 days</strong> with
                      no interest. Perfect for smaller treatments.
                    </p>
                    <p>
                      <strong>0% Interest</strong>
                    </p>
                  </div>
                </div>

                <div className="col-md-6 mb-4">
                  <div
                    className="text-center p-4"
                    style={{
                      background: '#f8f9fa',
                      borderRadius: '10px',
                      height: '100%',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '36px',
                        color: '#9b59b6',
                        marginBottom: '15px',
                      }}
                    >
                      <i className="fa fa-calendar" />
                    </div>
                    <h4>Payl8r Plans</h4>
                    <p>
                      Longer payment plans from <strong>3 to 12 months</strong> for
                      larger treatments. Make your dream treatments affordable.
                    </p>
                    <p>
                      <strong>Flexible Monthly Payments</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Representative Example */}
              <div
                className="mt-5 p-4"
                style={{
                  background: '#fff5f7',
                  borderRadius: '10px',
                  border: '1px solid #ffb3c6',
                }}
              >
                <h3 className="text-center mb-4">Representative Example</h3>
                <p className="text-center">
                  If you borrow <strong>£200</strong> over{' '}
                  <strong>12 months</strong> at a representative rate of{' '}
                  <strong>127.34% APR</strong> and an annual interest rate of{' '}
                  <strong>59.83% (fixed)</strong>, you will pay{' '}
                  <strong>12 monthly instalments of £21.83</strong>, with a
                  total amount payable of <strong>£261.96</strong>.
                </p>
                <p className="text-center text-muted" style={{ fontSize: '14px' }}>
                  Subject to creditworthiness and affordability assessments.
                </p>
              </div>

              {/* How It Works */}
              <div className="mt-5">
                <h3 className="text-center mb-4">How Payl8r Works</h3>
                <div className="row">
                  <div className="col-md-3 text-center mb-3">
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: '#ffb3c6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 15px',
                        fontSize: '20px',
                        fontWeight: 'bold',
                      }}
                    >
                      1
                    </div>
                    <p>
                      <strong>Book Your Treatment</strong>
                      <br />
                      Schedule your consultation or treatment with us
                    </p>
                  </div>
                  <div className="col-md-3 text-center mb-3">
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: '#ffb3c6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 15px',
                        fontSize: '20px',
                        fontWeight: 'bold',
                      }}
                    >
                      2
                    </div>
                    <p>
                      <strong>Choose Payl8r</strong>
                      <br />
                      Select Payl8r as your payment option
                    </p>
                  </div>
                  <div className="col-md-3 text-center mb-3">
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: '#ffb3c6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 15px',
                        fontSize: '20px',
                        fontWeight: 'bold',
                      }}
                    >
                      3
                    </div>
                    <p>
                      <strong>Quick Application</strong>
                      <br />
                      Simple online application with fast decision
                    </p>
                  </div>
                  <div className="col-md-3 text-center mb-3">
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: '#ffb3c6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 15px',
                        fontSize: '20px',
                        fontWeight: 'bold',
                      }}
                    >
                      4
                    </div>
                    <p>
                      <strong>Enjoy Your Results</strong>
                      <br />
                      Get your treatment and pay monthly
                    </p>
                  </div>
                </div>
              </div>

              {/* Eligible Treatments */}
              <div
                className="mt-5 p-4"
                style={{ background: '#f8f9fa', borderRadius: '10px' }}
              >
                <h3 className="text-center mb-4">
                  Treatments Available on Finance
                </h3>
                <div className="row text-center">
                  <div className="col-6 col-md-3 mb-3">
                    <a href="/lip-fillers" style={{ color: 'inherit' }}>
                      <i
                        className="fa fa-check-circle"
                        style={{ color: '#ffb3c6' }}
                      />{' '}
                      Lip Fillers
                    </a>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <a
                      href="/anti-wrinkle-injections-cheshunt"
                      style={{ color: 'inherit' }}
                    >
                      <i
                        className="fa fa-check-circle"
                        style={{ color: '#ffb3c6' }}
                      />{' '}
                      Anti-Wrinkle (Botox)
                    </a>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <a href="/dermal-fillers-cheshunt" style={{ color: 'inherit' }}>
                      <i
                        className="fa fa-check-circle"
                        style={{ color: '#ffb3c6' }}
                      />{' '}
                      Dermal Fillers
                    </a>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <a href="/cheek-fillers" style={{ color: 'inherit' }}>
                      <i
                        className="fa fa-check-circle"
                        style={{ color: '#ffb3c6' }}
                      />{' '}
                      Cheek Fillers
                    </a>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <a href="/russian-lips" style={{ color: 'inherit' }}>
                      <i
                        className="fa fa-check-circle"
                        style={{ color: '#ffb3c6' }}
                      />{' '}
                      Russian Lips
                    </a>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <a href="/tear-trough-filer" style={{ color: 'inherit' }}>
                      <i
                        className="fa fa-check-circle"
                        style={{ color: '#ffb3c6' }}
                      />{' '}
                      Tear Trough
                    </a>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <a href="/fat-dissolving" style={{ color: 'inherit' }}>
                      <i
                        className="fa fa-check-circle"
                        style={{ color: '#ffb3c6' }}
                      />{' '}
                      Fat Dissolving
                    </a>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <a href="/treatments" style={{ color: 'inherit' }}>
                      <i
                        className="fa fa-check-circle"
                        style={{ color: '#ffb3c6' }}
                      />{' '}
                      All Treatments
                    </a>
                  </div>
                </div>
              </div>

              {/* FAQ */}
              <div className="mt-5">
                <h3 className="text-center mb-4">
                  Frequently Asked Questions
                </h3>

                <div className="mb-4">
                  <h5>Is there a credit check?</h5>
                  <p>
                    Yes, Payl8r performs credit and affordability assessments as
                    part of the application process.
                  </p>
                </div>

                <div className="mb-4">
                  <h5>What happens if I miss a payment?</h5>
                  <p>
                    Missed payments may affect your credit file, future
                    borrowing and incur fees. Please ensure you can afford the
                    monthly payments before applying.
                  </p>
                </div>

                <div className="mb-4">
                  <h5>How do I apply?</h5>
                  <p>
                    Simply let us know you'd like to use Payl8r when you book or
                    at your appointment. We'll guide you through the quick
                    online application process.
                  </p>
                </div>

                <div className="mb-4">
                  <h5>Who can apply?</h5>
                  <p>
                    You must be 18 or over and a UK resident. Approval is
                    subject to creditworthiness and affordability assessments.
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center mt-5">
                <p>
                  <strong>
                    Ready to book your treatment with flexible payments?
                  </strong>
                </p>
                <a
                  href="https://booksy.com/en-gb/instant-experiences/widget/61001?instant_experiences_enabled=true&ig_ix=true&is_fb=1&fbclid=IwAR2p-lXjX6nvc-eD8ZQzHfOI9gXvf1O9J-HESKw62yYQR_VpYMfaU5QS4qo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-maincolor"
                >
                  Book Now
                </a>
                <p className="mt-3">
                  Or call us on{' '}
                  <a href="tel:07478705252">
                    <strong>07478705252</strong>
                  </a>{' '}
                  to discuss your options
                </p>
              </div>

              {/* FCA Disclaimer */}
              <div
                className="mt-5 p-4"
                style={{
                  background: '#f8f9fa',
                  borderRadius: '10px',
                  fontSize: '12px',
                  color: '#666',
                }}
              >
                <p className="mb-0 text-center">
                  <strong>Important Information:</strong> ADA Aesthetics LTD is
                  an Introducer Appointed Representative of Social Money Limited
                  t/a Payl8r who is authorised by the FCA under reference
                  675283. Credit is subject to credit and affordability
                  assessments. Missed payments may affect your credit file,
                  future borrowing and incur fees.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Appointment />
    </Layout>
  )
}

export default Finance

export const pageQuery = graphql`
  query FinanceQuery {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
